<!-- Über den Edittype (Input, Checkbox, Number, Select, Flughafensuche, Partnersuche, Zeitpicker, Widget oder Text) aus der Konfiguration wird das passende Element ausgewählt -->

<template>
  <b-form-input
    v-if="editable && editType === 'input'"
    v-model="wert"
    class="sidebar-field-control__input"
    placeholder="-"
  ></b-form-input>
  <div v-else-if="editType === 'checkbox'" class="sidebar-field-control__inline">
    <b-form-checkbox v-model="wert" :disabled="!editable" size="lg"></b-form-checkbox>
    <span v-if="append">{{ append }}</span>
  </div>
  <div v-else-if="editable && editType === 'number'" class="sidebar-field-control__inline">
    <span v-if="item.prepend">{{ item.prepend }}</span>
    <!-- Stepper mit Minus/Plus, Zahl und Einheit stehen mittig im Feld -->
    <div class="sidebar-field-control__affix">
      <button
        type="button"
        class="sidebar-field-control__step"
        aria-label="Verringern"
        :disabled="!(Number(wert) > 0)"
        @click="schritt(-1)"
      >
        <Minus :size="14" />
      </button>
      <label class="sidebar-field-control__wert">
        <b-form-input
          v-model="wert"
          class="sidebar-field-control__input--number"
          :style="{ width: eingabeBreite }"
          type="number"
          step="1"
          min="0.00"
          :number="true"
          placeholder="-"
        ></b-form-input>
        <span v-if="append" class="sidebar-field-control__append">{{ append }}</span>
      </label>
      <button type="button" class="sidebar-field-control__step" aria-label="Erhöhen" @click="schritt(1)">
        <Plus :size="14" />
      </button>
    </div>
  </div>
  <AppSelect
    v-else-if="editable && editType === 'select'"
    v-model="wert"
    sort="none"
    :options="options"
    :label="selectLabel"
    placeholder="-"
    clearable
    class="sidebar-field-control__select"
  />
  <AirportsSucheMultiselect
    v-else-if="editable && editType === 'flughafen'"
    v-model="wert"
    class="w-100 sidebar-field-control__flughafen"
    :isMultipleAllowed="true"
    :openDirection="'top'"
    :filterCountry="country"
    height="32px"
  ></AirportsSucheMultiselect>
  <CountrymanagerSucheMultiselect
    v-else-if="editable && editType === 'partner'"
    v-model="wert"
    placeholder="Weitere Partner suchen…"
    class="w-100"
    :isMultipleAllowed="true"
    :openDirection="'top'"
  />
  <DatePicker
    v-else-if="editable && editType === 'time'"
    v-model="wert"
    class="sidebar-field-control__time"
    type="time"
    input-class="form-control"
    format="HH:mm"
    value-type="format"
    :append-to-body="false"
  />
  <PaxWidget
    v-else-if="item.renderer === 'paxWidget'"
    class="sidebar-field-control__pax"
    :paxObject="item.value?.pax"
    :minPax="item.value?.metadata?.minPax"
    :maxPax="item.value?.metadata?.maxPax"
    :kalkPax="item.value?.metadata?.kalkPax"
    :forecastPax="item.value?.metadata?.forecastPax"
    :durchfuehrbareMinPax="item.value?.metadata?.durchfuehrbareMinPax"
  ></PaxWidget>
  <PrioritaetWidget v-else-if="item.renderer === 'prioritaetWidget'" :prioritaet="item.value" />
  <SidebarTerminplanungCountryManager
    v-else-if="item.renderer === 'countryManager'"
    :countryManagers="item.value"
  />
  <span v-else-if="item.renderer" v-html="item.renderer"></span>
  <span v-else class="sidebar-field-control__text">
    {{ anzeigewert }}
    <span v-if="append">{{ append }}</span>
  </span>
</template>

<script>
import PaxWidget from '@/components/produkte/reisetermine/pax-widget.vue';
import PrioritaetWidget from '@/components/common/prioritaet-widget.vue';
import SidebarTerminplanungCountryManager from '@/components/produkte/reisetermine/sidebar-terminplanung/sidebar-terminplanung-country-manager.vue';
import AirportsSucheMultiselect from '@/components/common/airports-suche-multiselect.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import CountrymanagerSucheMultiselect from '@/components/common/countrymanager-suche-multiselect.vue';
import DatePicker from 'vue2-datepicker';
import { Minus, Plus } from 'lucide-vue';

export default {
  name: 'SidebarFieldControl',
  components: {
    PaxWidget,
    PrioritaetWidget,
    SidebarTerminplanungCountryManager,
    AirportsSucheMultiselect,
    AppSelect,
    CountrymanagerSucheMultiselect,
    DatePicker,
    Minus,
    Plus,
  },
  props: {
    item: { type: Object, required: true },
    // null für einwertige Felder, sonst der Index innerhalb von value/valueEdited
    index: { type: Number, default: null },
    country: { type: String, default: null },
  },
  computed: {
    // Bei mehrwertigen Feldern stehen editType, editable und append als Array je Index
    editType() {
      // splitN-Felder sind Listen gleichartiger Zahlenwerte, etwa die Releasefristen
      if (this.item.splitN) {
        return 'number';
      }
      return this.indexWert(this.item.editType);
    },
    editable() {
      return this.indexWert(this.item.editable);
    },
    append() {
      return this.indexWert(this.item.append);
    },
    // Der zweite Wert eines Flughafen-Paars ist die Prio und wählt aus den gewählten Flughäfen
    options() {
      if (this.index === 1 && this.item.editType?.[0] === 'flughafen') {
        return this.item.valueEdited?.[0] ?? [];
      }
      return this.item.options;
    },
    selectLabel() {
      return Array.isArray(this.item.label) ? this.item.label[this.index] : this.item.label;
    },
    // Das Zahlenfeld wächst mit dem Inhalt, damit die Einheit direkt hinter der Zahl steht
    eingabeBreite() {
      const laenge = String(this.wert ?? '').length || 1;
      return `${laenge + 1}ch`;
    },
    anzeigewert() {
      const wert = this.index === null ? this.item.value : this.item.value?.[this.index];
      if (Array.isArray(wert)) {
        return wert.length > 0 ? wert.join(', ') : '-';
      }
      return wert ?? '-';
    },
    // Das Item stammt aus der Feldkonfiguration und ist geteilter Zustand
    // Der Setter schreibt valueEdited direkt und meldet die Änderung per input nach oben,
    // damit die Section den Änderungsstand gegen den Serverstand neu bewerten kann
    wert: {
      get() {
        return this.index === null ? this.item.valueEdited : this.item.valueEdited?.[this.index];
      },
      set(v) {
        if (this.index === null) {
          this.item.valueEdited = v;
        } else {
          this.$set(this.item.valueEdited, this.index, v);
        }
        this.$emit('input');
      },
    },
  },
  methods: {
    // Erhöht oder verringert den Zahlenwert um delta, nicht unter 0
    schritt(delta) {
      this.wert = Math.max(0, (Number(this.wert) || 0) + delta);
    },
    // Liefert bei einem Array den Wert des eigenen Index, sonst den Wert selbst
    indexWert(quelle) {
      return Array.isArray(quelle) && this.index !== null ? quelle[this.index] : quelle;
    },
  },
};
</script>

<style scoped>
.sidebar-field-control__pax.pax-widget {
  width: auto;
}
.sidebar-field-control__inline {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.sidebar-field-control__inline :deep(.custom-control) {
  min-height: auto;
}
/* Aktive Checkboxen zeigen Pointer und einen dezenten Hover auf der Box */
.sidebar-field-control__inline :deep(.custom-control-input:not(:disabled) ~ .custom-control-label) {
  cursor: pointer;
}
.sidebar-field-control__inline
  :deep(.custom-control-input:not(:disabled):not(:checked) ~ .custom-control-label:hover::before) {
  background-color: var(--surface-hover);
  border-color: var(--border-strong);
}
.sidebar-field-control__inline
  :deep(.custom-control-input:not(:disabled):checked ~ .custom-control-label:hover::before) {
  background-color: var(--primary-hover);
  border-color: var(--primary-hover);
}
.sidebar-field-control__input {
  height: var(--control-h-sm);
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--radius-sm);
}
.sidebar-field-control__affix {
  display: flex;
  align-items: center;
  flex: 0 1 104px;
  width: 104px;
  min-width: 0;
  height: var(--control-h-sm);
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}
.sidebar-field-control__affix:focus-within {
  border-color: var(--primary);
  box-shadow: var(--focus-ring);
}
.sidebar-field-control__step {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  height: 100%;
  padding: 0;
  color: var(--text-muted);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
}
.sidebar-field-control__step:hover:not(:disabled) {
  color: var(--text);
  background-color: var(--surface-hover);
}
.sidebar-field-control__step:disabled {
  color: var(--text-subtle);
  cursor: default;
}
.sidebar-field-control__wert {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  margin: 0;
  cursor: text;
}
.sidebar-field-control__input--number {
  max-width: 100%;
  height: auto;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  border: 0;
  box-shadow: none;
  -moz-appearance: textfield;
}
.sidebar-field-control__input--number:focus {
  box-shadow: none;
}
/* Spinner-Pfeile ausblenden, da sie sonst mit der Einheit kollidieren */
.sidebar-field-control__input--number::-webkit-outer-spin-button,
.sidebar-field-control__input--number::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.sidebar-field-control__append {
  font-size: 13px;
  color: var(--text-muted);
}
.sidebar-field-control__select {
  --app-select-height: var(--control-h-sm);
  --app-select-padding-x: 8px;
  --app-select-font-size: 13px;
}
.sidebar-field-control__flughafen {
  min-width: 0;
}
.sidebar-field-control__text {
  white-space: nowrap;
  text-align: right;
}
.sidebar-field-control__time {
  width: 104px;
  min-width: 0;
  height: var(--control-h-sm);
}
:deep(.mx-datepicker-content) {
  margin: 0;
}
/* input-class ersetzt die Klasse mx-input, deshalb wird das Feld über form-control angesprochen */
.sidebar-field-control__time :deep(.form-control) {
  height: var(--control-h-sm);
  min-height: var(--control-h-sm);
  padding: 4px 26px 4px 8px;
  font-size: 13px;
  border-radius: var(--radius-sm);
}
</style>
